import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
  UncontrolledTooltip,
  FormFeedback,
} from "reactstrap";
import {
  getAddOns,
  deleteAddOn,
  createAddOn,
  updateAddOn,
} from "../project/service";
import ConfirmationModal from "../project/ConfirmationModal";

const AddOnsList = () => {
  const params = useParams();
  const [addOns, setAddOns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [deleteAddOnConfirmationOpen, setDeleteAddOnConfirmationOpen] =
    useState(false);
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [currentAddOn, setCurrentAddOn] = useState({
    id: null,
    item: "",
    price: "",
  });

  useEffect(() => {
    getAddOnsForProject();
  }, []);

  const getAddOnsForProject = () => {
    setIsLoading(true);
    getAddOns({ projectId: params.projectId })
      .then((response) => setAddOns(response))
      .catch((err) => console.error("Error fetching add-ons:", err))
      .finally(() => setIsLoading(false));
  };

  const toggleAddEditModal = (addOn = null) => {
    setCurrentAddOn(addOn ? { ...addOn } : { id: null, item: "", price: "" });
    setAddEditModalOpen(!addEditModalOpen);
  };

  const handleSave = () => {
    const handleAddOn = currentAddOn.id ? updateAddOn : createAddOn;

    handleAddOn({
      projectId: params.projectId,
      ...currentAddOn,
    })
      .then(() => {
        setAddEditModalOpen(false);
        getAddOnsForProject();
      })
      .catch(({ error }) => alert(`Error saving add-on: ${error.statusText}`));
  };

  const toggleDeleteAddOnConfirmation = (item) => {
    setItemToDelete(item);
    setDeleteAddOnConfirmationOpen(!deleteAddOnConfirmationOpen);
  };

  const handleDeleteAddOn = () => {
    if (itemToDelete) {
      setIsLoading(true);
      deleteAddOn({
        projectId: params.projectId,
        id: itemToDelete.id,
      })
        .then(() => {
          setDeleteAddOnConfirmationOpen(false);
          setItemToDelete(null);
          getAddOnsForProject();
        })
        .catch(({ error }) => alert(`Error saving add-on: ${error.statusText}`))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <div className="p-2 m-2">
      <div className="d-flex m-2">
        {" "}
        <div className="fs-4">Project Add-Ons</div>
        <i
          className="fa-solid fa-circle-plus fs-4 px-2 py-1"
          onClick={() => toggleAddEditModal()}
          id="addAddOns"
        ></i>
        <UncontrolledTooltip target="addAddOns" placement="bottom">
          Click to add Addons{" "}
        </UncontrolledTooltip>
      </div>

      {isLoading ? (
        <Spinner color="primary" />
      ) : (
        <>
          <Table bordered hover responsive className="mt-3">
            <thead>
              <tr>
                <th className="text-center">S.No</th>
                <th className="text-center">Item</th>{" "}
                <th className="text-end">Price</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {addOns.length > 0 ? (
                addOns.map((addOn, index) => (
                  <tr key={addOn.id}>
                    <td className="text-center">{index + 1}</td>
                    <td className="text-center">{addOn.item}</td>
                    <td className="text-end">
                      ₹ {addOn.price.toLocaleString("en-IN")}
                    </td>

                    <td className="text-center m-0 px-0">
                      <Button
                        color="primary"
                        size="sm"
                        className="me-2"
                        onClick={() => toggleAddEditModal(addOn)}
                      >
                        Edit
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => toggleDeleteAddOnConfirmation(addOn)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Add-Ons added
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="d-flex justify-content-end mt-3">
            <div className="border p-3 rounded">
              <h5 className="mb-0">
                <strong>Total: </strong>₹{" "}
                {addOns
                  .reduce((sum, addOn) => sum + Number(addOn.price || 0), 0)
                  .toLocaleString("en-IN")}
              </h5>
            </div>
          </div>
        </>
      )}

      <Modal isOpen={addEditModalOpen} toggle={() => toggleAddEditModal()}>
        <ModalHeader toggle={() => toggleAddEditModal()}>
          {currentAddOn.id ? "Edit Add-On" : "Add Add-On"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="item">Item</Label>
            <Input
              id="item"
              type="text"
              value={currentAddOn.item}
              onChange={(e) =>
                setCurrentAddOn({ ...currentAddOn, item: e.target.value })
              }
              invalid={!currentAddOn.item.trim()}
            />
            {!currentAddOn.item.trim() && (
              <FormFeedback>Item cannot be empty.</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="price">Price</Label>
            <Input
              id="price"
              type="number"
              value={currentAddOn.price}
              onChange={(e) =>
                setCurrentAddOn({ ...currentAddOn, price: e.target.value })
              }
              invalid={isNaN(currentAddOn.price) || currentAddOn.price === ""}
            />
            {isNaN(currentAddOn.price) || currentAddOn.price === "" ? (
              <FormFeedback>Price must be a valid number.</FormFeedback>
            ) : null}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!currentAddOn.item.trim() || isNaN(currentAddOn.price)}
          >
            {currentAddOn.id ? "Update" : "Add"}
          </Button>
          <Button color="secondary" onClick={() => toggleAddEditModal()}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationModal
        isOpen={deleteAddOnConfirmationOpen}
        toggle={() => toggleDeleteAddOnConfirmation(null)}
        onConfirm={handleDeleteAddOn}
        message="Are you sure you want to delete this Add-On?"
        headingText="Confirm Deletion"
      />
    </div>
  );
};

export default AddOnsList;
